import { useState, type FormEvent } from 'react';
import type { CreatePaymentInput, Payment } from '@dsr/shared-types';
import {
  useCreatePayment,
  useLookup,
  usePayments,
  useProjects,
  useVoidPayment,
  type PaymentFilters,
} from '../api/queries/financials';
import { useOrganizations } from '../api/queries/clients';
import {
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Select,
  StatusBadge,
  Table,
  Textarea,
  type SelectOption,
} from '../components/ui';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { can } from '../auth/store';
import { ApiError } from '../api/client';
import { fmtDate } from '../utils/date';
import { fmtMoney } from '../utils/money';

const MONEY_RE = /^\d+(\.\d{1,2})?$/;

export function IncomePage(): JSX.Element {
  const [filters, setFilters] = useState<PaymentFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [voiding, setVoiding] = useState<Payment | null>(null);

  const { data, isLoading, isError } = usePayments(filters);
  const { data: orgsData } = useOrganizations({});
  const doVoid = useVoidPayment();

  const canWrite = can('payment.write');
  const canVoid = can('payment.void');
  const payments = data?.payments ?? [];

  const clearedTotal = payments
    .filter((p) => p.status === 'cleared')
    .reduce((sum, p) => sum + Number(p.amount), 0);

  // Radix Select forbids an empty value; use 'all' as the no-filter sentinel.
  const clientOptions: SelectOption[] = [
    { value: 'all', label: 'All clients' },
    ...(orgsData?.organizations ?? []).map((o) => ({
      value: String(o.id),
      label: o.name,
    })),
  ];
  const statusOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    { value: 'cleared', label: 'Cleared' },
    { value: 'pending', label: 'Pending' },
    { value: 'void', label: 'Void' },
  ];

  return (
    <div className="page income-page">
      <header className="page-header">
        <div>
          <h1>Income</h1>
          <p className="page-sub">
            {payments.length > 0
              ? `${payments.length} payments shown · ${fmtMoney(clearedTotal.toFixed(2))} cleared`
              : 'Money in — invoice payments and other revenue.'}
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Record income
          </Button>
        )}
      </header>

      <div className="filters-bar">
        <Select
          value={filters.organization_id ? String(filters.organization_id) : 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              organization_id: v === 'all' ? undefined : Number(v),
            }))
          }
          options={clientOptions}
          aria-label="Filter by client"
        />

        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              status: v === 'all' ? undefined : (v as Payment['status']),
            }))
          }
          options={statusOptions}
          aria-label="Filter by status"
        />
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {isError && <p className="error-text" role="alert">Failed to load payments.</p>}
      {!isLoading && !isError && payments.length === 0 && (
        <p className="muted">No payments match the current filters.</p>
      )}

      {payments.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>Date</Table.HeaderCell>
              <Table.HeaderCell>Client</Table.HeaderCell>
              <Table.HeaderCell>Project</Table.HeaderCell>
              <Table.HeaderCell>Invoice</Table.HeaderCell>
              <Table.HeaderCell>Method</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              {canVoid && <Table.HeaderCell aria-label="Actions" />}
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {payments.map((p) => (
              <Table.Row key={p.id}>
                <Table.Cell>{fmtDate(p.payment_date)}</Table.Cell>
                <Table.Cell tone="strong">{p.organization_name ?? '—'}</Table.Cell>
                <Table.Cell tone="muted">{p.project_title ?? '—'}</Table.Cell>
                <Table.Cell>{p.invoice_no ?? '—'}</Table.Cell>
                <Table.Cell>
                  {p.method}
                  {p.reference_no && <div className="muted small">{p.reference_no}</div>}
                </Table.Cell>
                <Table.Cell>{fmtMoney(p.amount)}</Table.Cell>
                <Table.Cell><StatusBadge status={p.status}>{p.status}</StatusBadge></Table.Cell>
                {canVoid && (
                  <Table.Cell>
                    {p.status !== 'void' && (
                      <Button variant="ghost" onClick={() => setVoiding(p)}>
                        Void
                      </Button>
                    )}
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {createOpen && <RecordIncomeModal onClose={() => setCreateOpen(false)} />}
      {voiding && (
        <ConfirmDialog
          open
          title="Void payment"
          message={`Void the ${fmtMoney(voiding.amount)} payment from ${fmtDate(voiding.payment_date)}? Voided payments stay in the ledger but count toward nothing.`}
          confirmLabel="Void"
          danger
          busy={doVoid.isPending}
          onConfirm={() => {
            doVoid
              .mutateAsync(voiding.id)
              .then(() => toast.success('Payment voided.'))
              .catch((err) =>
                toast.danger(err instanceof ApiError ? err.message : 'Void failed.'),
              )
              .finally(() => setVoiding(null));
          }}
          onCancel={() => setVoiding(null)}
        />
      )}
    </div>
  );
}

function RecordIncomeModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [orgId, setOrgId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState<number | null>(Date.now());
  const [method, setMethod] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [notes, setNotes] = useState('');

  const { data: orgsData } = useOrganizations({});
  const { data: projectsData } = useProjects(
    orgId === '' ? {} : { organization_id: orgId },
  );
  const { data: methods } = useLookup('payment_method');
  const create = useCreatePayment();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (orgId === '' || !date || !MONEY_RE.test(amount)) return;
    const input: CreatePaymentInput = {
      organization_id: orgId,
      amount,
      payment_date: date,
    };
    if (projectId !== '') input.project_id = projectId;
    if (method) input.method = method;
    if (referenceNo.trim()) input.reference_no = referenceNo.trim();
    if (notes.trim()) input.notes = notes.trim();

    try {
      await create.mutateAsync(input);
      toast.success('Income recorded.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to record income.');
    }
  };

  return (
    <Dialog
      title="Record income"
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="record-income-form"
            variant="primary"
            disabled={create.isPending || orgId === '' || !date || !MONEY_RE.test(amount)}
          >
            {create.isPending ? 'Saving…' : 'Record'}
          </Button>
        </>
      }
    >
      <form id="record-income-form" className="form-grid" onSubmit={onSubmit}>
        <p className="settings-hint form-field-full" style={{ margin: 0 }}>
          Invoice payments are recorded automatically when an invoice is marked
          paid — use this for other income (deposits, retainers funded outside
          the retainer page, misc revenue).
        </p>
        <FormField label="Client" required>
          <select
            className="form-input"
            value={orgId === '' ? '' : String(orgId)}
            onChange={(e) => {
              setOrgId(e.target.value === '' ? '' : Number(e.target.value));
              setProjectId('');
            }}
            required
          >
            <option value="">Select a client…</option>
            {(orgsData?.organizations ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Project">
          <select
            className="form-input"
            value={projectId === '' ? '' : String(projectId)}
            onChange={(e) => setProjectId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">None</option>
            {(projectsData?.projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.title}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Amount" required>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="2500.00"
            inputMode="decimal"
            required
          />
        </FormField>
        <FormField label="Date" required>
          <DateInput value={date} onChange={setDate} required />
        </FormField>
        <FormField label="Method">
          <select className="form-input" value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">Other</option>
            {(methods?.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Reference #">
          <Input
            value={referenceNo}
            onChange={(e) => setReferenceNo(e.target.value)}
            placeholder="check / txn number"
          />
        </FormField>
        <FormField label="Notes" full>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
